import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from './config/googleMapsConfig';
import { SearchRefreshProvider } from './context/SearchRefreshContext'; // Import the provider
import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { startBackgroundHealthCheck } from './utils/elasticsearchHealth';
import { Box, Center, Heading, VStack } from '@chakra-ui/react'; // Use VStack again
import './styles/chatbot-fixes.css'; // Import ChatBot fixes
import Layout from './components/Layout'
import Home from './pages/Home'
import Search from './pages/Search'
import SearchElasticsearch from './pages/SearchElasticsearch'
import SmartSearch from './pages/SmartSearch'
import Map from './pages/Map'
import MapElastic from './pages/map-elastic' // Added import for MapElastic
import SmartMap from './pages/SmartMap'
import SiteProfile from './pages/SiteProfile'
import SubLocationMapViewer from './pages/SubLocationMapViewer'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Dashboard from './pages/Dashboard'
import Drafts from './pages/Drafts'
import Account from './pages/Account'
import About from './pages/About'
import Contact from './pages/Contact'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import DmcaPage from './pages/DmcaPage'
import RequestCreditPage from './pages/RequestCreditPage'
import CopyrightPage from './pages/CopyrightPage'
import NotFound from './pages/NotFound'
import CreateSite from './pages/CreateSite'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import Admin from './pages/Admin'
import AdminFlags from './pages/AdminFlags'
import TestSiteInfo from './pages/TestSiteInfo'
import GlobalTimelinePage from './pages/GlobalTimeline'
import { Globe } from './components/loading/Globe'; // Import the Globe component

const libraries = ['places'];

export default function App() {
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false); // Track if auth state is resolved

  // Start background health monitoring
  useEffect(() => {
    const cleanup = startBackgroundHealthCheck();
    return cleanup; // Cleanup on unmount
  }, []);

  useEffect(() => {
    const minLoadingTime = 5000; // Minimum 5 seconds loading time
    const startTime = Date.now();

    // Simulate additional loading tasks if needed, e.g., fetching initial data
    // const fetchData = async () => { await someAsyncTask(); };
    // fetchData();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = minLoadingTime - elapsedTime;

      if (remainingTime > 0) {
        setTimeout(() => {
          setAuthChecked(true); // Mark auth as checked
        }, remainingTime);
      } else {
        setAuthChecked(true); // Mark auth as checked immediately if min time passed
      }
    });

    return () => unsubscribe();
  }, []);

  // Only set loading to false when auth is checked
  useEffect(() => {
    if (authChecked) {
      setLoading(false);
    }
  }, [authChecked]);

  // Render main app structure immediately, overlay loading screen if loading is true
  return (
    <>
      {loading && (
        <Center
          position="fixed" // Keep overlay fixed on screen
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="black" // Dark background for the overlay
          zIndex="overlay" // Ensure it's on top
          opacity={1}
          transition="opacity 0.5s ease-out" // Optional fade-out
          // Add pointerEvents="none" if needed after fade-out to allow interaction below
        >
          {/* Use VStack centered within the Center component */}
          <VStack
            spacing={{ base: 2, md: 4 }} // Reduced spacing on mobile
            w="full"
            maxW="600px" // Max width for the VStack content
            h="full" // Try taking full height of Center
            justify="center" // Center content vertically on all devices
            px={4}
          >
            <Heading
              as="h1"
              size={{ base: "lg", md: "xl" }}
              color="white"
              textAlign="center"
              flexShrink={0}
              mb={{ base: 2, md: 4 }}
            >
              Megalith Maps
            </Heading>
            {/* Box for the globe with explicit size, constrained by viewport */}
            <Box
              w={{ base: "350px", md: "500px" }} // Smaller on mobile for better fit
              h={{ base: "350px", md: "500px" }} // Smaller on mobile for better fit
              maxW="85vw" // Max width relative to viewport
              maxH="60vh" // Consistent max height
              flexShrink={0}
              position="relative"
            >
              <Globe /> {/* Globe should fill this Box */}
            </Box>
          </VStack>
        </Center>
      )}
      {/* Render the main app content regardless of loading state */}
      {/* It will be hidden behind the overlay initially */}
      <LoadScript
        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        libraries={libraries}
      >
        <SearchRefreshProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
              <Route path="/search" element={<SmartSearch />} />
              <Route path="/search-elasticsearch" element={<SearchElasticsearch />} />
              <Route path="/search-standard" element={<Search />} />
              <Route path="/map" element={<SmartMap />} />
              <Route path="/map-elastic" element={<MapElastic />} /> {/* Added route for MapElastic */}
              <Route path="/map-standard" element={<Map />} />
              <Route path="/site/:id" element={<SiteProfile />} />
              <Route path="/site/:id/sublocations" element={<SubLocationMapViewer />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/dmca" element={<DmcaPage />} />
              <Route path="/request-credit" element={<RequestCreditPage />} />
              <Route path="/copyright" element={<CopyrightPage />} />
              <Route
                path="/create-site"
                element={
                  <ProtectedRoute>
                    <CreateSite />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-draft/:draftId"
                element={
                  <ProtectedRoute>
                    <CreateSite />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/drafts"
                element={
                  <ProtectedRoute>
                    <Drafts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/flags"
                element={
                  <AdminRoute>
                    <AdminFlags />
                  </AdminRoute>
                }
              />
              <Route path="/test-site-info" element={<TestSiteInfo />} />
              <Route path="/timeline" element={<GlobalTimelinePage />} />
              <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
        </SearchRefreshProvider>
    </LoadScript>
    </> // Correctly placed closing fragment tag
  ); // Closing parenthesis for the return statement
}
